// libs/shared/ui-components/src/AuditLog/auditLogSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import firebaseService from '@ameroservices-platform/shared/services/firebase';
import type { AuditLogRecord } from './AuditLogPanel.types';

interface AuditLogUser {
  id: string;
  name: string;
}

interface AuditLogState {
  records: AuditLogRecord[];
  users: Record<string, AuditLogUser>;
}

const initialState: AuditLogState = {
  records: [],
  users: {},
};

const auditLogSlice = createSlice({
  name: 'shared/auditLog',
  initialState,
  reducers: {
    setRecords(state, action: PayloadAction<AuditLogRecord[]>) {
      state.records = action.payload;
    },
    addUser(state, action: PayloadAction<AuditLogUser>) {
      state.users[action.payload.id] = action.payload;
    },
  },
});

export const { setRecords, addUser } = auditLogSlice.actions;

export default auditLogSlice.reducer;

export function mapDoc(doc: any): AuditLogRecord {
  const docData = doc.data();
  const base = {
    id: doc.id,
    changedAt: docData.changedAt.toDate().toString(),
    changedAtRaw: docData.changedAt.seconds
  };

  // New format: has 'type' field ('created' | 'updated' | 'deleted')
  if ('type' in docData) {
    return { ...docData, ...base };
  }

  // Legacy format: has 'before' and 'after' as JSON strings
  return {
    ...docData,
    ...base,
    before: JSON.parse(docData.before ?? '{}'),
    after: JSON.parse(docData.after ?? '{}')
  };
}

export const requestAuditLog =
  (type: string, affectedUid: string) => (dispatch: any) => {
    const db = firebaseService.getAuditLogRootDB();
    return db
      .collection(type)
      .where('affectedUid', '==', affectedUid)
      .onSnapshot((querySnapshot: any) => {
        const records: AuditLogRecord[] = [];
        querySnapshot.forEach((doc: any) => records.push(mapDoc(doc)));
        dispatch(setRecords(records));
      });
  };

export const requestAuditLogLimited =
  (type: string, affectedUid: string, orderBy?: string, limit?: number) =>
  (dispatch: any) => {
    const db = firebaseService.getAuditLogRootDB();
    let query: any = db
      .collection(type)
      .where('affectedUid', '==', affectedUid);
    if (orderBy) query = query.orderBy(orderBy, 'desc');
    if (limit) query = query.limit(limit);
    return query.onSnapshot((querySnapshot: any) => {
      const records: AuditLogRecord[] = [];
      querySnapshot.forEach((doc: any) => records.push(mapDoc(doc)));
      dispatch(setRecords(records));
    });
  };

export const requestUser =
  (userId: string, isAmeroAdmin: boolean) => async (dispatch: any) => {
    let name: string;
    if (isAmeroAdmin) {
      const rootDb = firebaseService.getRootDB();
      const doc = await rootDb.collection('users').doc(userId).get();
      const data = doc.data();
      name = data?.data?.displayName ?? data?.data?.email ?? 'Ukendt';
    } else {
      const db = firebaseService.getOrganisationRootDB();
      const doc = await db.collection('organisation_users').doc(userId).get();
      const data = doc.data();
      name = data?.displayName ?? data?.email ?? 'Amero';
    }
    dispatch(addUser({ id: userId, name }));
  };

export const selectAuditLogRecords = (state: any): AuditLogRecord[] =>
  state.shared.auditLog.records;

export const selectAuditLogUsers = (state: any): Record<string, AuditLogUser> =>
  state.shared.auditLog.users;
